import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import {
  CheckCircle2,
  FileCheck,
  Info,
  MessageSquareText,
  ReceiptText,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import { useState } from "react";

import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";

import { Can } from "@/components/can";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCurrentUser } from "@/hooks/use-current-user";

import {
  useCloseClaimFromAgentFeedback,
  useSaveAgentFeedbackAdditionalLoss,
  useSetAdditionalLossRequestDate,
} from "../../api/use-agent-feedback";
import type { ClaimWithRelations } from "../../api/use-claim";
import { ExpensesList } from "../expenses-list";
import type { ClaimOverview } from "./agent-feedback-steps";
import { buildAdditionalLossPayload } from "./default-additional-loss-form";

/**
 * Alert shown above the flow while additional losses are awaited — port of the
 * MountedTeleport block of FormAdditionalLoss.vue (#teleport-agent-feedback-alert).
 */
export function AdditionalLossRequestAlert({
  claim,
  claimOverview,
}: {
  claim: ClaimWithRelations;
  claimOverview: ClaimOverview;
}) {
  const setRequestDate = useSetAdditionalLossRequestDate(claim.id);

  if (!claimOverview.travelAgencyCompanyName && !claimOverview.isOfficeB2b) {
    return null;
  }

  const form = claimOverview.form;

  if (!form?.additionalLossRequestSentAt) {
    return (
      <Alert variant="warning">
        <TriangleAlert />
        <AlertTitle className="flex items-baseline justify-between gap-2">
          <span>Attente des préjudices supplémentaires</span>
          <span className="text-xs font-normal text-gray-500">
            Le {format(new Date(form?.updatedAt || form?.createdAt || 0), "dd/MM/yyyy")}
          </span>
        </AlertTitle>
        <AlertDescription>
          <p>Nous avons besoin de votre retour afin de constituer d'éventuels dommages supplémentaires</p>
          <div className="py-4">
            <Button
              variant="outline"
              onClick={() => setRequestDate.mutate()}
              disabled={setRequestDate.isPending}
            >
              J'ai envoyé la demande de préjudices
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Alert variant="info">
      <Info />
      <AlertTitle className="flex items-baseline justify-between gap-2">
        <span>Vous avez indiqué avoir pris contact avec l'entreprise</span>
        <span className="text-xs font-normal text-gray-500">
          Le {format(new Date(form.additionalLossRequestSentAt), "dd/MM/yyyy")}
        </span>
      </AlertTitle>
    </Alert>
  );
}

interface FormAdditionalLossProps {
  claim: ClaimWithRelations;
  claimOverview: ClaimOverview;
  onNextStep: (increment: number) => void;
}

/** Port of components/agent-feedback/FormAdditionalLoss.vue. */
export function FormAdditionalLoss({ claim, claimOverview, onNextStep }: FormAdditionalLossProps) {
  const navigate = useNavigate();
  const { roles } = useCurrentUser();

  const saveAdditionalLoss = useSaveAgentFeedbackAdditionalLoss(claim.id);
  const closeClaimMutation = useCloseClaimFromAgentFeedback(claim.id);

  const [note, setNote] = useState("");

  const disabledForAgent = !claimOverview.form?.b2c && roles.includes("CUSTOMER_AGENT");
  const customerNoteData = claim.customerNotes;

  async function submitForm() {
    try {
      const { success } = await saveAdditionalLoss.mutateAsync(buildAdditionalLossPayload(note));
      if (success) {
        onNextStep(1);
      }
    } catch {
      // Error toast handled by notify.promise.
    }
  }

  async function closeClaim() {
    try {
      await closeClaimMutation.mutateAsync(buildAdditionalLossPayload(note || "Abandon"));
      void navigate({ to: "/claims", search: { claimId: claim.id, claimTab: "overview" } });
    } catch {
      // Error toast handled by notify.promise.
    }
  }

  if (disabledForAgent) {
    return (
      <Alert variant="success">
        <CheckCircle2 />
        <AlertTitle>
          L’équipe chargée de comptes s’occupe de cette partie et d’obtenir les signatures
        </AlertTitle>
      </Alert>
    );
  }

  return (
    <div className="px-2">
      {customerNoteData ? (
        <Can permissions={["CUSTOMER_ACCOUNT_MANAGER", ...UPCLAIM_ROLES]}>
          <div className="border-b pt-4 pb-6">
            <h2 className="mb-5 inline-flex text-base leading-7 font-semibold text-gray-900">
              <MessageSquareText className="mt-1 mr-2 h-5 w-5 text-gray-700" />
              Notes Upclaim à destination de l'agence :
            </h2>
            <div className="mt-2">
              <blockquote className="prose mt-2 mb-3 px-6 text-base font-medium whitespace-pre-line">
                « <span className="italic">{customerNoteData}</span> »
              </blockquote>
            </div>
          </div>
        </Can>
      ) : null}

      <div className="mt-2">
        <div className="col-span-full gap-x-6 gap-y-8 sm:grid-cols-6">
          <form className="-mx-8" onSubmit={(event) => event.preventDefault()}>
            <ExpensesList
              claim={claim}
              claimExpenses={claim.claimExpenses}
              showActions
              header={
                <>
                  <div className="px-3 text-left">
                    <dt className="text-left text-base leading-6 font-medium text-gray-900">
                      <span className="inline-flex items-center">
                        <ReceiptText className="mr-2 h-5 w-5 text-gray-700" />
                        Nous communiquer des dépenses
                      </span>
                    </dt>
                  </div>
                  <div className="my-6 px-6 text-sm leading-7 text-gray-700">
                    <p>
                      En plus d'une potentielle indemnisation forfaitaire de{" "}
                      <span className="font-semibold text-gray-700">
                        {claimOverview.ec261CompensationAmount}€
                      </span>{" "}
                      par voyageur (règlement européen 261/2004), des{" "}
                      <span className="font-semibold text-gray-700">
                        frais supplémentaires pourraient être remboursés
                      </span>
                      .
                    </p>
                    {claimOverview.travelAgencyCompanyName && !claimOverview.isLeisureOffice ? (
                      <div className="mt-6">
                        <p>Entreprise impactée :</p>
                        <ul className="list-disc px-4 font-medium text-gray-800">
                          <li className="mt-2 list-inside">{claimOverview.travelAgencyCompanyName}</li>
                        </ul>
                      </div>
                    ) : null}
                  </div>
                </>
              }
            />
          </form>

          <div className="mt-3 px-2">
            <Alert variant="info">
              <Info />
              <AlertTitle>
                Si les passagers ont subis un préjudice particulier, merci de nous l'indiquer dans la note
                ci-dessous
              </AlertTitle>
            </Alert>
          </div>

          <div className="my-12 border-t border-gray-900/10" />
        </div>
      </div>

      <div className="mt-10">
        <Label htmlFor="additional-loss-note" className="block text-base font-medium text-gray-900">
          Nous joindre une note
        </Label>
        <div className="mt-2">
          <Textarea
            id="additional-loss-note"
            rows={4}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </div>
      </div>

      <div className="mt-6 flex items-center justify-end gap-x-6">
        <Button variant="outline" onClick={() => void closeClaim()} disabled={closeClaimMutation.isPending}>
          <Trash2 className="mr-1 h-4 w-4" />
          Clôturer
        </Button>
        <Button type="submit" onClick={() => void submitForm()} disabled={saveAdditionalLoss.isPending}>
          <FileCheck className="mr-1 h-4 w-4" />
          Valider les dépenses
        </Button>
      </div>
    </div>
  );
}
